import {
  Component,
  createMemo,
  createUniqueId,
  For,
  JSXElement,
  mergeProps,
  Show,
} from "solid-js";
import { DOMElement } from "solid-js/jsx-runtime";

import { IssueDisplay, useForm } from "./FormContext.js";

interface TextAreaEvent extends Event {
  currentTarget: HTMLTextAreaElement;
  target: DOMElement;
}

interface TextAreaProps {
  value?: string | null;
  editable?: boolean;
  name?: string;
  rows?: number;
  onInput?: (value: string | number, event: TextAreaEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  onClick?: (e: MouseEvent) => void;
  class?: string;
  labelClass?: string;
  label?: string | JSXElement;
  placeholder?: string;
  autocomplete?: string;
}

export const TextArea: Component<TextAreaProps> = (unmergedProps) => {
  const props = mergeProps({ rows: 5 }, unmergedProps);

  const commonClassName = createMemo(
    () => `border rounded-md ${props.editable === false ? "" : "p-2"}`,
  );
  const labelClass = createMemo(
    () => `text-gray-500 ${props.labelClass ?? ""}`,
  );

  const id = createUniqueId();

  const formContext = useForm();

  const getValue = () => {
    if (formContext && props.name) {
      return formContext.getData(props.name);
    }
    return props.value;
  };

  const valueChanged = (event: TextAreaEvent) => {
    props.onInput?.(event.currentTarget.value, event);

    if (formContext && props.name) {
      return formContext.setData(props.name, event.currentTarget.value);
    }
  };

  const getFormErrors = () => {
    if (!props.name) {
      return false;
    }

    const errors = formContext?.getErrors(props.name);
    // sometimes this returns true, which means the field is valid
    if (errors && errors !== true) {
      return errors;
    }
    return formContext?.errors?.()?.[props.name] ?? false;
  };

  const relevantIssues = () => formContext?.getIssues(props.name);

  return (
    <div class="flex w-full flex-col">
      <Show when={props.label}>
        <label for={id} class={labelClass()}>
          {props.label}
        </label>
      </Show>
      <Show
        when={props.editable || props.editable === undefined}
        fallback={
          <div
            class={`whitespace-pre-line border-transparent ${commonClassName()}`}
          >
            {getValue() || props.placeholder || ""}
          </div>
        }
      >
        <textarea
          autocomplete={props.autocomplete}
          id={id}
          placeholder={props.placeholder}
          name={formContext ? undefined : props.name}
          class={`rounded-md ${commonClassName()}`}
          classList={{
            "border-error-500 outline-error-500":
              Boolean(getFormErrors()) || Boolean(relevantIssues()?.length),
          }}
          rows={props.rows}
          onInput={valueChanged}
          onKeyDown={props.onKeyDown}
          onKeyUp={props.onKeyUp}
          onClick={props.onClick}
          value={getValue() || ""}
        />
      </Show>
      <Show when={relevantIssues()}>
        <p class="text-sm text-red-500 lg:col-span-2 lg:col-start-2">
          <IssueDisplay issues={relevantIssues()} />
        </p>
      </Show>
      <Show when={getFormErrors() !== false}>
        <p class="text-sm text-error-500 lg:col-span-2 lg:col-start-2">
          <For each={getFormErrors()}>{(error) => <p>{error}</p>}</For>
        </p>
      </Show>
    </div>
  );
};
